// IndividualJobTrigger.jsx - Disparo de coleta ADN (por certificado) para uma empresa.
// A coleta de produção é via ADN (incremental por NSU); o scraping por login/senha está
// desabilitado (CAPTCHA). Para empresa COM certificado válido oferecemos duas ações:
// "Coletar agora" (incremental) e "Reprocessar últimos 3 meses". Sem cert válido, o
// disparo fica bloqueado com aviso.
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "@astryxdesign/core/Card";
import { Stack, HStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { Spinner } from "@astryxdesign/core/Spinner";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { downloadLogsService } from "../../services/downloadLogsService";
import { isFinishedJobStatus } from "../../utils/downloadStatusUtils";

/**
 * @param {Object} props
 * @param {Object} props.company - Empresa (espera-se `cert_status`: none|valid|expiring|expired)
 * @param {Function} props.onJobStarted
 * @param {Function} props.onRefresh
 * @param {Function} props.onJobProgressUpdate
 */
const IndividualJobTrigger = ({
  company,
  onJobStarted,
  onRefresh,
  onJobProgressUpdate,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [chunkProgress, setChunkProgress] = useState(null);

  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  // Empresa coleta via certificado (ADN) quando tem cert válido ou a vencer.
  const hasValidCert =
    company?.cert_status === "valid" || company?.cert_status === "expiring";

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []);

  const refreshCompanyNotes = async () => {
    try {
      const response = await downloadLogsService.getCompanyNotes(company.id, {
        limit: 25,
        offset: 0,
      });
      if (response.success && onRefresh) onRefresh();
    } catch (err) {
      console.error(
        `[IndividualJobTrigger] Erro ao atualizar notas da empresa ${company.id}:`,
        err,
      );
    }
  };

  const pollJobStatus = async (jobId) => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    setCurrentJobId(jobId);

    const poll = async () => {
      try {
        const response = await downloadLogsService.getDownloadJobStatus(jobId);
        if (response.success && response.job) {
          const status = response.job.status;
          setJobStatus(status);

          const results =
            response.job.results && typeof response.job.results === "object"
              ? response.job.results
              : null;
          if (results?.chunkProgress) setChunkProgress(results.chunkProgress);
          else if (isFinishedJobStatus(status)) setChunkProgress(null);

          if (onJobProgressUpdate) onJobProgressUpdate(response.job);

          if (isFinishedJobStatus(status)) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            isPollingRef.current = false;
            await refreshCompanyNotes();
            setCurrentJobId(null);
            setJobStatus(null);
            setChunkProgress(null);
          }
        }
      } catch (err) {
        console.error("[IndividualJobTrigger] Erro no polling:", err);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        isPollingRef.current = false;
        setCurrentJobId(null);
        setJobStatus(null);
        setChunkProgress(null);
      }
    };

    pollingIntervalRef.current = setInterval(poll, 3000);
    poll();
  };

  const handleOpenModal = () => {
    setError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  /**
   * Dispara a coleta ADN. adnMode: "incremental" (resume do último NSU) ou
   * "reprocess3m" (reseta o NSU e re-coleta os últimos 3 meses).
   */
  const handleAdnCollect = async (adnMode) => {
    if (adnMode === "reprocess3m") {
      const ok = window.confirm(
        `Reprocessar os últimos 3 meses de ${company.name}? A coleta será refeita a partir do início do período (sem duplicar notas já existentes).`,
      );
      if (!ok) return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await downloadLogsService.startIndividualDownload(
        company.id,
        null,
        null,
        false,
        { adnMode },
      );
      if (response.success) {
        if (onJobStarted) onJobStarted(response.jobId);
        pollJobStatus(response.jobId);
        handleCloseModal();
      } else {
        setError(response.message || "Erro ao iniciar a coleta");
      }
    } catch (err) {
      console.error("Erro ao iniciar coleta ADN:", err);
      setError("Erro interno ao iniciar a coleta");
    } finally {
      setLoading(false);
    }
  };

  const jobRunning =
    currentJobId && jobStatus && !isFinishedJobStatus(jobStatus);

  return (
    <>
      {/* Banner de disparo */}
      <Card padding={4} width="60%">
        <HStack gap={3} vAlign="center" hAlign="between">
          <Stack gap={0.5}>
            <Text weight="semibold">Coleta de Notas</Text>
            <Text type="supporting">
              Coleta via certificado (ADN), incremental por NSU
            </Text>
            {currentJobId && jobStatus && (
              <Stack gap={0.5}>
                <HStack gap={2} vAlign="center">
                  <Spinner size="sm" label="Coleta em andamento" />
                  <Text size="sm" color="accent">
                    Job {currentJobId} - Status: {jobStatus}
                  </Text>
                </HStack>
                {chunkProgress && (
                  <Text type="supporting">
                    Bloco {chunkProgress.index}/{chunkProgress.total}
                    {chunkProgress.label ? ` (${chunkProgress.label})` : ""}
                    {chunkProgress.status ? ` — ${chunkProgress.status}` : ""}
                  </Text>
                )}
              </Stack>
            )}
          </Stack>
          <Button
            label={loading ? "Iniciando..." : "Coletar Notas"}
            variant="primary"
            icon={<FontAwesomeIcon icon="download" />}
            isDisabled={loading || jobRunning}
            onClick={handleOpenModal}
          />
        </HStack>
      </Card>

      {/* Modal */}
      {showModal && (
        <Dialog
          isOpen
          onOpenChange={(open) => {
            if (!open && !loading) handleCloseModal();
          }}
          width={480}
        >
          <Stack gap={3}>
            <DialogHeader
              title="Coletar notas via ADN"
              subtitle={`${company.name} • CNPJ: ${company.cnpj}`}
              onOpenChange={(open) => {
                if (!open && !loading) handleCloseModal();
              }}
              hasDivider
            />

            {error && <Banner status="error" title={error} />}

            {hasValidCert ? (
              <>
                <Banner
                  status="info"
                  title="A coleta usa o certificado digital (ADN) e é incremental: traz apenas o que chegou desde a última coleta. Use “Reprocessar últimos 3 meses” para refazer o período."
                />
                <Button
                  label={loading ? "Iniciando..." : "Coletar agora (incremental)"}
                  variant="primary"
                  icon={<FontAwesomeIcon icon="download" />}
                  isLoading={loading}
                  onClick={() => handleAdnCollect("incremental")}
                />
                <Button
                  label="Reprocessar últimos 3 meses"
                  variant="secondary"
                  icon={<FontAwesomeIcon icon="sync" />}
                  isDisabled={loading}
                  onClick={() => handleAdnCollect("reprocess3m")}
                />
              </>
            ) : (
              <Banner
                status="warning"
                title="Coleta indisponível para esta empresa"
                description="A coleta por login/senha está temporariamente desabilitada. Cadastre um certificado digital válido para coletar via ADN."
              />
            )}

            <HStack gap={2} hAlign="end">
              <Button
                label="Fechar"
                variant="ghost"
                isDisabled={loading}
                onClick={handleCloseModal}
              />
            </HStack>
          </Stack>
        </Dialog>
      )}
    </>
  );
};

export default IndividualJobTrigger;
